import { useState, useEffect } from 'react';
import { usePage, Link, router } from '@inertiajs/react';
import { useUser } from '../../src/context/UserContext';
import { Notifications, showNotification } from '@mantine/notifications';
import Helmet from 'react-helmet';
import InteractionButtons from '../../components/Posts/InteractionButtons';
import {
  Container,
  Text,
  Group,
  Image,
  Badge,
  Spoiler,
  Divider,
  Flex,
  Box,
  ActionIcon,
  Tooltip,
  Button,
  Grid,
  TypographyStylesProvider,
} from '@mantine/core';
import ManageBadge from '../../components/ManageBadge';
import ArtistCard from '../../components/Cards/ArtistCard';
import {
  IconMapPin,
  IconNavigationPin,
  IconWorld,
  IconTicket,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandMeetup,
  IconBrandTiktok,
  IconChevronRight,
  IconArrowUpRight,
  IconCheck,
  IconX,
  IconRepeat,
  IconBookmark,
} from '@tabler/icons-react';
import { GoogleMiniMap } from '../../components/Maps/GoogleMiniMap';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import ArtistDrawerContent from '../../components/Drawer/ArtistDrawerContent';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import RecurringEventInstances from '../../components/RecurringEventInstances';
import EventSchema from '../../components/Maps/EventSchema'; // Import the EventSchema component

import '../../styles/EventShowView.css';

interface Host {
  id: number;
  name: string;
  slug: string;
}
interface Artist {
  id: number;
  name: string;
  slug: string;
  avatarUrl?: string;
}

interface RecurringEventInstance {
  id: number;
  slug: string;
  name: string;
  event_start_date: string;
  event_end_date: string;
  active?: boolean;
  pending?: boolean;
}

interface RecurringEventProps {
  recurringEvent: {
    id: number;
    num_occurrences: number;
    events: RecurringEventInstance[];
  };
  userHasAccess: boolean;
  onEventClick: (event: RecurringEventInstance) => void;
}

interface DanceStyle {
  id: number;
  name: string;
}

interface PageProps {
  page_meta?: {
    title?: string;
    description?: string;
    url?: string;
    image?: string;
  };
  event: {
    id: number;
    name: string;
    slug: string;
    imageUrl?: string;
    description: string;
    approve_url: string;
    event_start_date: string;
    event_end_date: string;
    dance_styles: DanceStyle[];
    event_status_id: number;
    user: number;
    is_recurring: boolean;
    website_url?: string;
    ticket_url?: string;
    facebook_url?: string;
    instagram_url?: string;
    meetup_url?: string;
    tiktok_url?: string;
    latitude: number;
    longitude: number;
    city?: { name: string };
    state?: { name: string };
    country?: { name: string; flag_emoji: string };
    continent?: string;
    area?: {
      name: string;
      slug: string;
      strip_area_suffix: boolean;
      country_id: number;
      featured: boolean;
      events_count: number;
    };
  };
  hosts: Host[];
  artists: Artist[];
  tags: string[];
  location: {
    address: string;
    city: { name: string };
    state: string;
    country: { name: string; flag_emoji: string };
    continent: { name: string };
  };
  userHasAccess: boolean;
  recurring_event?: {
    id: number;
    num_occurrences: number;
    events: RecurringEventInstance[];
  };
}

export default function EventShow() {
  const { event, hosts, artists, tags, location, userHasAccess, page_meta, recurring_event } =
    usePage<PageProps>().props;
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<'event' | 'artist'>('event');
  const [selectedRecurringEvent, setSelectedRecurringEvent] = useState<any>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);

  const { user } = useUser();
  const isAdmin = user?.roles.includes('SuperAdmin');
  const isOwner = user?.id === event.user;
  const canSeeStatus = isAdmin || isOwner;
  const isLoggedIn = !!user;

  // console.log('Is Admin:', isAdmin);
  // console.log('Is Owner:', isOwner);
  // console.log('User:', user);
  // console.log('Event:', event);
  // console.log('Hosts:', hosts);
  // console.log('Event User:', event.user);

  // console.log('Location:', location);
  // console.log('Approval URL:', event.approve_url);

  // Helper function to format dates in ISO-8601 format for schema
  const formatSchemaDate = (dateString: string): string => {
    // Parse the date string to ensure it's a proper date object
    const date = new Date(dateString);

    // Format to ISO string with timezone info
    // Example: 2024-01-16T20:00:00.000Z -> 2024-01-16T20:00:00+00:00
    // Replace the 'Z' (which means UTC) with the actual offset
    const isoString = date.toISOString();

    // For UTC times (which end in Z), convert to explicit +00:00 format
    return isoString.replace('Z', '+00:00');
  };

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to bookmark events',
        color: 'red',
        autoClose: 5000,
      });
      return;
    }

    // Toggle bookmark state optimistically
    setIsBookmarked((prevState) => !prevState);

    // Make request to the upvote endpoint
    fetch(`/events/${event.slug}/upvote.json`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': token || '',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to update bookmark status');
        return response.json();
      })
      .then((data) => {
        // Show success notification
        showNotification({
          title: isBookmarked ? 'Bookmark Removed' : 'Event Bookmarked',
          message: isBookmarked
            ? `"${event.name}" has been removed from your bookmarks`
            : `"${event.name}" has been added to your bookmarks`,
          color: 'green',
          autoClose: 5000,
        });

        // Update localStorage
        const bookmarkedEventsSlugs = JSON.parse(localStorage.getItem('bookmarkedEvents') || '[]');
        if (!isBookmarked) {
          if (!bookmarkedEventsSlugs.includes(event.slug)) {
            bookmarkedEventsSlugs.push(event.slug);
          }
        } else {
          const index = bookmarkedEventsSlugs.indexOf(event.slug);
          if (index > -1) {
            bookmarkedEventsSlugs.splice(index, 1);
          }
        }
        localStorage.setItem('bookmarkedEvents', JSON.stringify(bookmarkedEventsSlugs));
      })
      .catch((error) => {
        console.error('Error updating bookmark:', error);

        // Revert optimistic update
        setIsBookmarked((prevState) => !prevState);

        // Show error notification
        showNotification({
          title: 'Error',
          message: 'Failed to update bookmark status',
          color: 'red',
          autoClose: 5000,
        });
      });
  };

  // Prepare the location data for Schema markup
  const prepareSchemaLocation = () => {
    // For in-person events (default for your site)
    return {
      name: location.address,
      address: {
        streetAddress: location.address,
        addressLocality: location.city?.name || '',
        addressRegion: location.state || '',
        addressCountry: location.country?.name || '',
        // If you have postal code info, add it here
        postalCode: '',
      },
    };
  };

  // Prepare organizer data from hosts
  const prepareSchemaOrganizer = () => {
    if (hosts.length === 0) return null;

    // Use the first host as the primary organizer
    const mainHost = hosts[0];
    return {
      name: mainHost.name,
      url: `${window.location.origin}/o/${mainHost.slug}`,
    };
  };

  // Prepare performers data from artists
  const prepareSchemaPerformers = () => {
    if (artists.length === 0) return null;

    // Create an array of performer objects
    if (artists.length === 1) {
      // For a single artist, return a single object
      return {
        '@type': 'PerformingGroup',
        name: artists[0].name,
      };
    } else {
      // For multiple artists, return an array of performer objects
      return artists.map((artist) => ({
        '@type': 'PerformingGroup',
        name: artist.name,
      }));
    }
  };

  // Get event status in schema.org format
  const getSchemaEventStatus = () => {
    // Map your status IDs to Schema.org status values
    const statusMap = {
      1: 'https://schema.org/EventScheduled', // Active
      2: 'https://schema.org/EventScheduled', // Pending
      3: 'https://schema.org/EventCancelled', // Rejected
    };

    return statusMap[event.event_status_id] || 'https://schema.org/EventScheduled';
  };

  // Prepare event images for schema
  const prepareSchemaImages = () => {
    if (!event.imageUrl) return null;

    // Return the image URL as an array (Google recommends multiple sizes but we'll use what we have)
    return [event.imageUrl];
  };

  // Prepare ticket/offer information for schema
  const prepareSchemaOffers = () => {
    if (!event.ticket_url) return null;

    return {
      url: event.ticket_url,
      // Since you mentioned you don't have ticket info yet, we'll set some defaults
      price: 0, // You can update this when you have price info
      priceCurrency: 'USD', // Default currency, update as needed
      availability: 'https://schema.org/InStock',
    };
  };

  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setDrawerContentType('artist');
    setDrawerOpened(true);
  };

  const renderDrawerContent = () => {
    return selectedArtist ? <ArtistDrawerContent artist={selectedArtist} /> : null;
  };

  const handleRecurringEventClick = (eventInstance: any) => {
    // Fetch the event data (or you might need to make an API call here)
    setSelectedRecurringEvent(eventInstance);
    setDrawerContentType('event');
    setDrawerOpened(true);
  };

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC', // Forces display in UTC time
    });
  };

  // Format date range
  const formatDateRange = () => {
    const startDate = new Date(event.event_start_date);
    const endDate = new Date(event.event_end_date);
    if (startDate.toDateString() === endDate.toDateString()) {
      return formatDate(event.event_start_date);
    }
    return `${formatDate(event.event_start_date)} - ${formatDate(event.event_end_date)}`;
  };

  const isPastEvent = new Date(event.event_end_date) < new Date();

  // Format month for the date box
  const getMonthAbbreviation = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  };

  // Format day for the date box
  const getDayOfMonth = (dateString: string) => {
    const date = new Date(dateString);
    return date.getDate();
  };

  // Get day of week and full date
  const getFullDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const areSameDates = (startDateStr: string, endDateStr: string) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    return (
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate()
    );
  };

  const month = getMonthAbbreviation(event.event_start_date);
  const day = getDayOfMonth(event.event_start_date);
  const fullStartDate = getFullDisplayDate(event.event_start_date);
  const fullEndDate = getFullDisplayDate(event.event_end_date);
  const startTime = formatTime(event.event_start_date);
  const endTime = formatTime(event.event_end_date);

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`;
  console.log('Map URL:', mapUrl);
  const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content;

  // Common content components
  const imageContent = (
    <>
      <Notifications position="top-right" />
      {canSeeStatus && isLoggedIn && (
        <Group align="center" gap="md" mb="sm">
          {/* Status Badge */}
          {(() => {
            const meta = {
              1: { label: 'Active', color: 'green' },
              2: { label: 'Pending', color: 'yellow' },
              3: { label: 'Rejected', color: 'red' },
            }[event.event_status_id] || { label: 'Unknown', color: 'gray' };

            return (
              <Badge color={meta.color} size="lg" variant="light" style={{ textTransform: 'none' }}>
                Event Status: {meta.label}
              </Badge>
            );
          })()}

          {/* Approve Button, only for admins & pending */}
          {isAdmin && event.event_status_id === 2 && (
            <Button
              color="green"
              variant="light"
              fullWidth
              size="lg"
              radius="md"
              onClick={() =>
                router.put(
                  `/events/${event.slug}/approve`,
                  {},
                  {
                    preserveScroll: true,
                    headers: {
                      'X-CSRF-Token': token || '',
                    },
                    onSuccess: () => {
                      showNotification({
                        title: 'Success',
                        message: `Event "${event.name}" has been approved!`,
                        color: 'green',
                        icon: <IconCheck />, // You might need to import this
                        autoClose: 5000,
                        position: 'top-right',
                      });
                    },
                    onError: (error) => {
                      showNotification({
                        title: 'Error',
                        message: `Failed to approve event: ${error.message || 'Unknown error'}`,
                        color: 'red',
                        icon: <IconX />,
                        autoClose: 5000,
                      });
                    },
                  },
                )
              }
            >
              Approve This Event
            </Button>
          )}
        </Group>
      )}

      {/* The actual image with bookmark icon overlay */}
      <Box style={{ position: 'relative' }}>
        {event.imageUrl ? (
          <Image src={event.imageUrl} alt={event.name} fit="cover" height={200} radius="sm" />
        ) : (
          <Text>No Image</Text>
        )}

        {/* Bookmark Icon */}
        {isLoggedIn && (
          <ActionIcon
            variant="transparent"
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              width: 35,
              height: 35,
              zIndex: 10,
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '50%',
            }}
            onClick={handleBookmarkToggle}
          >
            <IconBookmark
              size={28}
              style={{
                fill: isBookmarked ? '#EA580C' : 'transparent',
                color: isBookmarked ? '#EA580C' : 'white',
              }}
            />
          </ActionIcon>
        )}
      </Box>
    </>
  );

  useEffect(() => {
    if (user) {
      // Check localStorage for bookmark status
      const bookmarkedEventsSlugs = JSON.parse(localStorage.getItem('bookmarkedEvents') || '[]');
      setIsBookmarked(bookmarkedEventsSlugs.includes(event.slug));
    }
  }, [event.slug, user]);

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }

    // console.log('Event Page meta:', page_meta);
    console.log('Is event recurring?', event.is_recurring);
    console.log('Recurring event data:', recurring_event);

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  const eventLinksContent = (
    <>
      <Group gap="xs" mt="-12">
        {event.website_url && (
          <Tooltip label="Website">
            <ActionIcon component="a" href={event.website_url} target="_blank" rel="noopener" variant="transparent">
              <IconWorld size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}

        {event.ticket_url && (
          <Tooltip label="Tickets">
            <ActionIcon component="a" href={event.ticket_url} target="_blank" rel="noopener" variant="transparent">
              <IconTicket size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}

        {event.facebook_url && (
          <Tooltip label="Facebook">
            <ActionIcon
              component="a"
              href={event.facebook_url}
              target="_blank"
              rel="noopener"
              variant="noopener"
              variant="transparent"
            >
              <IconBrandFacebook size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}

        {event.instagram_url && (
          <Tooltip label="Instagram">
            <ActionIcon component="a" href={event.instagram_url} target="_blank" rel="noopener" variant="transparent">
              <IconBrandInstagram size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}

        {event.meetup_url && (
          <Tooltip label="Meetup">
            <ActionIcon component="a" href={event.meetup_url} target="_blank" rel="noopener" variant="transparent">
              <IconBrandMeetup size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}

        {event.tiktok_url && (
          <Tooltip label="TikTok">
            <ActionIcon component="a" href={event.tiktok_url} target="_blank" rel="noopener" variant="transparent">
              <IconBrandTiktok size={28} color="#EA580C" />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Box w="100%" mt="12px">
        <InteractionButtons context={['event']} isUserSignedIn={isLoggedIn} object={event} />
      </Box>
    </>
  );

  const tagsContent = (
    <>
      <div style={{ marginBottom: '0px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          Event Tags{' '}
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={12} />
      <Group gap="xs">
        {tags.map((tag) => (
          <Badge key={tag} color="#EA580C" size="md" className="tag-badge">
            {tag}
          </Badge>
        ))}
      </Group>
    </>
  );

  const contactContent = (
    <>
      <div className="mt-4">
        <Text size="sm" className="text-white">
          Contact Host
        </Text>
        <Text size="sm" className="text-white">
          Report Event
        </Text>
      </div>
    </>
  );

  const titleContent = <Text className="rubik-header">{event.name}</Text>;

  const aboutContent = (
    <>
      <div style={{ marginBottom: '0px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          Details{' '}
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={6} />
      <Spoiler maxHeight={160} mb="3rem" showLabel="Read more" hideLabel="Show less" color="orange">
        <TypographyStylesProvider className="new-event-description">
          <div className="rich-text-post" dangerouslySetInnerHTML={{ __html: event.description }} />
        </TypographyStylesProvider>
      </Spoiler>
    </>
  );

  const detailsContent = (
    <>
      {/* Heading + divider stay, per your preference */}
      <div style={{ marginBottom: '0px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          About Event
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={12} />

      {/* Date / time / address block */}
      <Flex mih={50} gap="0" justify="flex-start" align="flex-start" direction="column" wrap="wrap">
        {/* Date + time row */}
        <Flex align="center" gap="md" mb="0" mt="0">
          {/* Date box */}
          <Box
            style={{
              backgroundColor: '#222',
              borderRadius: '8px',
              width: '48px',
              height: '48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 0,
              margin: 0,
              color: '#fff',
              flexShrink: 0,
              overflow: 'hidden',
              border: '1px solid #75757530',
            }}
          >
            {/* month */}
            <Box style={{ backgroundColor: '#ea580c50', width: '100%', textAlign: 'center', padding: '1px 0' }}>
              <Text size="sm" fw={600} style={{ lineHeight: 1.1, color: '#fff' }}>
                {month}
              </Text>
            </Box>

            {/* day */}
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, width: '100%' }}>
              <Text size="xl" fw={700} style={{ lineHeight: 0 }}>
                {day}
              </Text>
            </Box>
          </Box>

          {/* friendly date + time */}
          <div>
            <Text size="md" fw={600} style={{ color: '#f2f2f2', margin: 0, padding: 0, lineHeight: 1 }}>
              {areSameDates(event.event_start_date, event.event_end_date)
                ? fullStartDate
                : `${fullStartDate} - ${fullEndDate}`}
            </Text>
            <Text size="sm" style={{ color: '#ccc', marginTop: 8, lineHeight: 1.5 }}>
              {startTime}–{endTime}
            </Text>
          </div>
        </Flex>

        {/* address row */}
        <Flex align="center" gap="10px" mt="8px">
          <Box
            style={{
              backgroundColor: '#222',
              borderRadius: '8px',
              width: '48px',
              height: '48px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 0,
              color: '#fff',
              border: '1px solid #75757530',
              flexShrink: 0,
            }}
          >
            <IconMapPin size={28} color="#ea580c" />
          </Box>

          <Text
            size="md"
            fw={600}
            sx={(theme) => ({
              width: '100%',
              [theme.fn.largerThan('sm')]: { width: 400 },
            })}
            style={{ color: '#f2f2f2', lineHeight: 1.2 }}
          >
            {location.address}
          </Text>

          <Tooltip label="Open in Google Maps">
            <ActionIcon
              component="a"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`}
              target="_blank"
              variant="transparent"
            >
              <IconNavigationPin size={28} color="#ea580c" />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Flex>
      {/* Render recurring events component if recurringEvent exists */}
      {recurring_event && (
        <RecurringEventInstances
          recurringEvent={recurring_event}
          userHasAccess={userHasAccess}
          onEventClick={handleRecurringEventClick}
        />
      )}
    </>
  );

  const artistsContent = (
    <div>
      <div style={{ marginBottom: '0px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          Artists{' '}
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={12} />

      <Grid gutter="8px">
        {artists.map((a) => (
          <Grid.Col span={{ base: 6, sm: 6, md: 3 }} key={a.id}>
            <ArtistCard key={a.id} artist={a} onCardClick={handleArtistClick} />
          </Grid.Col>
        ))}
      </Grid>
    </div>
  );

  const danceStyleContent = (
    <>
      <div style={{ marginBottom: '0px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          Dance Styles{' '}
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={12} />
      <Group gap="xs">
        {event.dance_styles.map((style) => (
          <Badge key={style.id} color="#EA580C" size="md" className="tag-badge">
            {style.name}
          </Badge>
        ))}
      </Group>
    </>
  );

  const locationContent = (
    <>
      <div style={{ marginBottom: 0 }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
          Location
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={12} />

      {/* interactive 400‑px map */}
      <GoogleMiniMap lat={event.latitude} lng={event.longitude} height={250} linkUrl={mapUrl} />

      {/* optional textual address below the map */}
      <Text size="sm" className="mt-3 text-white">
        {location.address}, {location.country.flag_emoji} {location.continent.name}
      </Text>
    </>
  );

  const areaName = event.area
    ? event.area.strip_area_suffix
      ? // remove trailing “Area” (with optional preceding space), case‑insensitive
        event.area.name.replace(/\s*\bArea\b\s*$/i, '')
      : event.area.name
    : '';

  const areaContent = event?.area ? (
    <Link href={`/city/${event?.area?.slug}`}>
      <Button
        component="button" // Changed from "a" to "button" since it's now inside a Link
        variant="filled"
        color="#EA580C"
        radius="md"
        size="xs"
        styles={{
          root: {
            padding: '4px 8px',
            display: 'inline-flex',
            alignItems: 'center',
          },
          leftIcon: { marginRight: 4 },
          rightIcon: { marginLeft: 4 },
          label: { fontSize: '0.875rem', fontWeight: 500 },
        }}
        leftSection={<IconMapPin size={14} />}
        rightSection={<IconChevronRight size={14} />}
      >
        Listed in {areaName}
      </Button>
    </Link>
  ) : null;

  const hostedContent =
    hosts.length > 0 ? (
      <>
        <div style={{ marginBottom: '0px' }}>
          <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
            Hosted By{' '}
          </Text>
        </div>
        <Divider color="#757575" mt={0} mb={6} />
        <Group gap="xs" mt="sm">
          {hosts.map((h) => (
            <Link key={h.id} href={`/o/${h.slug}`}>
              <Badge
                variant="light"
                rightSection={<IconArrowUpRight size={16} />}
                color="white"
                size="xl"
                className="org-tag-badge"
              >
                {h.name}
              </Badge>
            </Link>
          ))}
        </Group>
      </>
    ) : null;

  const debugSchemaData = () => {
    // Format dates
    const formattedStartDate = formatSchemaDate(event.event_start_date);
    const formattedEndDate = formatSchemaDate(event.event_end_date);
    console.log('Raw date:', event.event_start_date);
    console.log('Formatted start date:', formattedStartDate);
    console.log('Formatted end date:', formattedEndDate);

    // Location data
    const locationData = prepareSchemaLocation();
    console.log('Location data:', locationData);

    // Image data
    const imageData = prepareSchemaImages();
    console.log('Image data:', imageData);

    // Offers data
    const offersData = prepareSchemaOffers();
    console.log('Offers data:', offersData);

    // Performer data
    const performerData = prepareSchemaPerformers();
    console.log('Performer data:', performerData);

    // Organizer data
    const organizerData = prepareSchemaOrganizer();
    console.log('Organizer data:', organizerData);

    // Event status
    const statusData = getSchemaEventStatus();
    console.log('Event status:', statusData);

    // Complete schema object
    const completeSchema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.name,
      description: page_meta?.description || event.description.replace(/<[^>]*>/g, ''),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      location: locationData,
      image: imageData,
      offers: offersData,
      performer: performerData,
      organizer: organizerData,
      eventStatus: statusData,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    };

    // Log in different formats for easier debugging
    console.log('Complete schema object:', completeSchema);
    console.log('Complete schema JSON-LD:', JSON.stringify(completeSchema, null, 2));

    // Show what the script tag would look like
    console.log('JSON-LD script tag that will be added to head:');
    console.log(`<script type="application/ld+json">
  ${JSON.stringify(completeSchema, null, 2)}
  </script>`);

    return completeSchema;
  };

  // Run the debug function to log everything
  const schemaData = debugSchemaData();

  return (
    <Container size="xl" py="xl">
      {/* Add the EventSchema component */}
      <EventSchema
        name={event.name}
        description={page_meta?.description || event.description.replace(/<[^>]*>/g, '')} // Strip HTML
        startDate={formatSchemaDate(event.event_start_date)}
        endDate={formatSchemaDate(event.event_end_date)}
        location={prepareSchemaLocation()}
        image={prepareSchemaImages()}
        offers={prepareSchemaOffers()}
        performer={prepareSchemaPerformers()}
        organizer={prepareSchemaOrganizer()}
        eventStatus={getSchemaEventStatus()}
        eventAttendanceMode="https://schema.org/OfflineEventAttendanceMode" // Default to offline for the 95% case
      />
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <div className="event-page-wrapper">
        <div className="manage-header">
          {userHasAccess && (
            <ManageBadge
              entityType="event"
              entitySlug={event.slug}
              userHasAccess
              editUrl={`/events/${event.slug}/edit`}
            />
          )}
        </div>
        <div className="event-container">
          {/* Mobile view - sections directly in container for ordering */}
          <div className="event-mobile">
            <div className="event-section event-area">{areaContent}</div>
            <div className="event-section event-image">{imageContent}</div>
            <div className="event-section event-links">{eventLinksContent}</div> {/* new */}
            {hosts.length > 0 && <div className="event-section event-hosted">{hostedContent}</div>}
            <div className="event-section event-title">{titleContent}</div>
            <div className="event-section event-tags">{tagsContent}</div>
            <div className="event-section event-tags">{danceStyleContent}</div>
            <div className="event-section event-about">{aboutContent}</div>
            <div className="event-section event-details">{detailsContent}</div>
            {artists.length > 0 && <div className="event-section event-artists">{artistsContent}</div>}
            <div className="event-section event-location">{locationContent}</div>
            <div className="event-section event-contact">{contactContent}</div>
          </div>

          {/* Desktop view - two column layout */}
          <div className="event-sidebar">
            <div className="event-section event-image">{imageContent}</div>
            <div className="event-section event-links">{eventLinksContent}</div> {/* new */}
            {hosts.length > 0 && <div className="event-section event-hosted">{hostedContent}</div>}
            <div className="event-section event-tags">{danceStyleContent}</div>
            <div className="event-section event-tags">{tagsContent}</div>
            <div className="event-section event-contact">{contactContent}</div>
          </div>

          <div className="event-main">
            <div className="event-section event-area">{areaContent}</div>

            <div className="event-section event-title">{titleContent}</div>
            <div className="event-section event-details">{detailsContent}</div>
            <div className="event-section event-about">{aboutContent}</div>
            {artists.length > 0 && <div className="event-section event-artists">{artistsContent}</div>}
            <div className="event-section event-location">{locationContent}</div>
          </div>
        </div>
      </div>

      {selectedArtist && drawerContentType === 'artist' && (
        <DynamicDrawer
          opened={drawerOpened}
          type="artist"
          data={selectedArtist}
          onClose={() => setDrawerOpened(false)}
        />
      )}

      {selectedRecurringEvent && drawerContentType === 'event' && (
        <DynamicDrawer
          opened={drawerOpened}
          type="event"
          data={selectedRecurringEvent}
          onClose={() => setDrawerOpened(false)}
        />
      )}
    </Container>
  );
}
